import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    Output,
    TemplateRef,
} from '@angular/core';
import { translate, TranslocoService } from '@jsverse/transloco';
import { IFacetNodeModel, selectTranslate, Severity, SeverityHelper } from '@libs/vc-core-lib';
import Highcharts, { PointClickEventObject, PointOptionsObject, SeriesPieOptions } from 'highcharts';
import { PieChartComponent } from '../../pie-chart/pie-chart.component';

@Component({
    selector: 'vc-findings-by-severity',
    imports: [CommonModule, PieChartComponent],
    templateUrl: './findings-by-severity-chart.component.html',
    styleUrl: './findings-by-severity-chart.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FindingsBySeverityChartComponent {
    private readonly _transloco = inject(TranslocoService);
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);

    private _title: string = translate('LANDING_DASHBOARD.FINDINGS_BY_SEVERITY.TITLE');

    series: SeriesPieOptions[] = [
        {
            name: this._title,
            data: [],
            type: 'pie',
        },
    ];

    constructor() {
        selectTranslate(
            this._transloco,
            () => {
                this._title = translate('LANDING_DASHBOARD.FINDINGS_BY_SEVERITY.TITLE');
                if (this._facets) {
                    this._renderFindingsBySeverityChart(this._facets);
                }
                this._changeDetectorRef.markForCheck();
            },
            this._destroyRef
        );
    }

    readonly legendOptions = { symbolHeight: 8, symbolWidth: 8, symbolRadius: 4, itemMarginBottom: 4 };

    @ContentChild('chartWrapperEnd')
    chartWrapperEndTemplate!: TemplateRef<unknown>;

    @Output()
    pointClick: EventEmitter<PointClickEventObject> = new EventEmitter<PointClickEventObject>();

    @Input()
    loading = false;

    @Input()
    set title(value: string) {
        this._title = value;
        this.series[0].name = value;
    }
    get title(): string {
        return this._title;
    }

    @Input()
    contentHeight!: string;

    @Input()
    set facets(value: IFacetNodeModel[]) {
        if (value) {
            this._renderFindingsBySeverityChart(value);
        }
        this._facets = value;
    }
    get facets(): IFacetNodeModel[] {
        return this._facets;
    }
    private _facets!: IFacetNodeModel[];

    @Input()
    onPointClick!: (event: PointClickEventObject) => void;

    @Input()
    tooltipFormat?: string;

    private _renderFindingsBySeverityChart(severityFacets: IFacetNodeModel[]): void {
        this.series[0].name = this._title;
        const severityFacetData: PointOptionsObject[] = [];
        const severities = Object.values(Severity).reverse();
        severityFacets.forEach((facetNode) => {
            severityFacetData.push({
                name: this.severityRenderer(facetNode.value as Severity),
                y: Number(facetNode.count),
                custom: { severity: facetNode.value },
                color: SeverityHelper.getSeverityColor(facetNode.value as Severity),
            });
        });
        if (this.series) {
            this.series[0].data = [...severityFacetData].sort(
                (a, b) =>
                    severities.indexOf((a.custom as Highcharts.Dictionary<Severity>).severity) -
                    severities.indexOf((b.custom as Highcharts.Dictionary<Severity>).severity)
            );
        }
        this.series = [...this.series];
    }

    severityRenderer = (severity: Severity) => SeverityHelper.getSeverityLocale(severity);
}
